import * as _ from 'lodash';
import sheriffApi from '@/api/SheriffApi';
import router from '@/router';
import { uuid } from 'vue-uuid';
import moment from 'moment';
import Vue from 'vue';
import LoginApi from '../../api/LoginApi';
import CustomerApi from '../../api/CustomersApi';

const state = {
  ownerParticipantId: null,
  activeChallenge: null,
  challengeOwner: null,
  activeParticipant: null,
  allParticipants: [],
  isLoggedInCustomer: false,
  loading: false,
};

const getters = {
  challengeAmount: state => state.activeChallenge && state.activeChallenge.challengeAmount,
  challengeStartDate: state => state.activeChallenge && state.activeChallenge.startDate,
  participantId: state => (state.activeParticipant ? state.activeParticipant.participantId : 'Not Connected'),
  participantName: state => state.activeParticipant && state.activeParticipant.firstName,
  participantSpent: state => (state.activeParticipant ? state.activeParticipant.spent : 0),
  participantIcon: state => state.activeParticipant && state.activeParticipant.icon,
  participantNudgeAnswers: state => state.activeParticipant && state.activeParticipant.nudgeAnswers,
  isChallengeOwner: (state, getters) => state.ownerParticipantId === getters.participantId,
  daysPassed: (state, getters) => (getters.challengeStartDate ? moment().diff(moment(getters.challengeStartDate).startOf('day'), 'days') : 0),
  daysLeft: (state, getters) => 7 - getters.daysPassed,
  isChallengeFinished: (state, getters) => getters.daysPassed > 7,
  leftToSpend: (state, getters) => getters.challengeAmount - getters.participantSpent,
  isCustomer: state => state.activeParticipant && !!state.activeParticipant.customerId,
  participantsNicknames: state => _.map(state.allParticipants, p => p.firstName),
  nudgeQuestions: state => state.activeChallenge.nudgeQuestions,
  currentNudgeQuestion: (state, getters) => (getters.challengeStartDate && getters.nudgeQuestions ? getters.nudgeQuestions[getters.daysPassed] : ''),
  currentNudgeAnswer: (state, getters) => {
    if (getters.currentNudgeQuestion && getters.participantNudgeAnswers) {
      const nudgeAnswer = _.find(getters.participantNudgeAnswers, { questionIndex: getters.daysPassed });
      return nudgeAnswer ? nudgeAnswer.answer : null;
    } return null;
  },
};

const actions = {
  async answerNudgeQuestion({ commit, state, getters }, answer) {
    commit('addNudgeAnswer', { answer, questionText: getters.currentNudgeQuestion, questionIndex: getters.daysPassed });
    await sheriffApi.updateParticipant(state.activeParticipant);
  },
  async initChallengeCreation({ dispatch, state }) {
    await dispatch('fetchCustomerParticipant');
    if (state.activeParticipant) {
      await dispatch('fetchActiveChallenge');
    }
  },
  setActiveParticipantByName({ commit, dispatch }, activeParticipantName) {
    const activeParticipant = _.find(state.allParticipants, { firstName: activeParticipantName });
    commit('setActiveParticipant', activeParticipant);
    dispatch('saveParticipantNameToLocalStorage', activeParticipantName);
  },
  setActiveParticipantByCustomerId({ commit }, customerId) {
    const activeParticipant = _.find(state.allParticipants, { customerId });
    commit('setActiveParticipant', activeParticipant);
  },
  setActiveParticipantByNicknameFromLocalStorage({ commit, dispatch }) {
    const participantName = localStorage.getItem('riseup.activeParticipantName');
    if (participantName) {
      dispatch('setActiveParticipantByName', participantName);
    }
  },
  saveParticipantNameToLocalStorage({ commit }, participantName) {
    localStorage.setItem('riseup.activeParticipantName', participantName);
  },
  async initChallengeDashboard({ dispatch, state }, customerId) {
    this.loading = true;
    if (customerId) {
      await dispatch('initChallengeDashboardForCustomer', customerId);
    } else {
      await dispatch('initChallengeDashboardNonCustomer');
    }
    this.loading = false;
  },
  async initChallengeDashboardNonCustomer({ dispatch, state }) {
    await dispatch('fetchActiveChallenge');
    await dispatch('fetchAllParticipants');
    const participantName = localStorage.getItem('riseup.activeParticipantName');
    if (participantName) {
      dispatch('setActiveParticipantByName', participantName);
    }
    if (state.activeParticipant) {
      await dispatch('fetchCustomerLoginStatus');
      if (state.isLoggedInCustomer) {
        const { customerId } = await CustomerApi.getCustomerDetails();
        if (!_.find(state.allParticipants, p => p.customerId && p.customerId === customerId)) {
          await dispatch('connectParticipantToCustomer');
        }
      }
    }
  },
  async initChallengeDashboardForCustomer({ dispatch, state }, customerId) {
    await dispatch('fetchActiveChallenge');
    await dispatch('fetchAllParticipants');
    dispatch('setActiveParticipantByCustomerId', customerId);
    if (!state.activeParticipant) {
      dispatch('setActiveParticipantByNicknameFromLocalStorage');
      if (state.activeParticipant) {
        await dispatch('connectParticipantToCustomer');
      }
    }
  },

  async joinChallenge({ commit, dispatch, state }, { nickname, isCustomer }) {
    const participantId = uuid.v4();
    if (isCustomer) {
      await sheriffApi.createParticipantForCustomer(participantId, nickname, state.ownerParticipantId);
    } else {
      await sheriffApi.createParticipant(participantId, nickname, state.ownerParticipantId);
    }
    await sheriffApi.addParticipantsToChallenge([participantId], state.ownerParticipantId);
    dispatch('saveParticipantNameToLocalStorage', nickname);
  },
  disconnect({ commit, dispatch }) {
    commit('setActiveParticipant', null);
    dispatch('saveParticipantNameToLocalStorage', null);
  },
  goToChallengeDashboard({ state }) {
    router.push(`/challenges/${state.ownerParticipantId}`);
  },
  async createChallengeWithGoal({ commit, state, dispatch }, { challengeGoal, activeMemberFirstName }) {
    const ownerParticipantId = uuid.v4();
    await sheriffApi.createOwnerParticipant(ownerParticipantId, activeMemberFirstName);
    const activeChallenge = await sheriffApi.createChallenge(challengeGoal, ownerParticipantId);
    dispatch('saveParticipantNameToLocalStorage', activeMemberFirstName);
    commit('setOwnerParticipantId', ownerParticipantId);
    commit('setActiveChallenge', activeChallenge);
  },
  async connectParticipantToCustomer({ commit, getters }) {
    const activeParticipant = await sheriffApi.connectParticipantToCustomer(getters.participantId);
    commit('setActiveParticipant', activeParticipant);
  },
  async fetchCustomerParticipant({ commit }) {
    const activeParticipant = await sheriffApi.getParticipantForCurrentCustomer();
    commit('setActiveParticipant', activeParticipant);
    if (activeParticipant.nudgeAnswers) {
      commit('setNudgeAnswers', activeParticipant.nudgeAnswers);
    }
  },
  async fetchActiveChallenge({ commit, state }) {
    const activeChallenge = await sheriffApi.getActiveChallengeForMember(state.ownerParticipantId);
    commit('setActiveChallenge', activeChallenge);
  },
  async fetchAllParticipants({ commit, state }) {
    const allParticipants = await sheriffApi.getChallengeParticipants(state.ownerParticipantId);
    commit('setAllParticipants', allParticipants);
  },
  async fetchCustomerLoginStatus({ commit }) {
    const isLoggedInCustomer = await LoginApi.isLoggedIn();
    commit('setIsLoggedInCustomer', isLoggedInCustomer);
  },
  async updateParticipantSpent({ commit, getters, dispatch }, sum) {
    const updatedParticipant = await sheriffApi.updateParticipantSpent(sum, getters.participantId);
    commit('setActiveParticipant', updatedParticipant);
    dispatch('fetchAllParticipants');
  },
  async startChallenge({ commit, state, dispatch }) {
    await commit('setLoading', true);
    await sheriffApi.startChallenge(state.ownerParticipantId);
    await dispatch('fetchActiveChallenge');
    await commit('setLoading', false);
  },
};

const mutations = {
  setOwnerParticipantId(state, ownerParticipantId) {
    state.ownerParticipantId = ownerParticipantId;
  },
  setLoading(state, loading) {
    state.loding = loading;
  },
  setIsLoggedInCustomer(state, isLoggedInCustomer) {
    state.isLoggedInCustomer = isLoggedInCustomer;
  },
  addNudgeAnswer(state, nudgeAnswer) {
    if (state.activeParticipant.nudgeAnswers) {
      state.activeParticipant.nudgeAnswers.push(nudgeAnswer);
    } else {
      Vue.set(state.activeParticipant, 'nudgeAnswers', [nudgeAnswer]);
    }
  },
  setAllParticipants(state, allParticipants) {
    state.allParticipants = allParticipants;
  },
  setActiveChallenge(state, activeChallenge) {
    state.activeChallenge = activeChallenge;
  },
  setActiveParticipant(state, activeParticipant) {
    state.activeParticipant = activeParticipant;
  },
  setNudgeAnswers(state, nudgeAnswers) {
    state.nudgeAnswers = nudgeAnswers;
  },
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
